import { RefCallback, useCallback, useEffect, useRef } from 'react'

interface UseInfiniteScrollOptions {
  /**
   * Callback invoked when the sentinel becomes visible.
   * Should trigger the data-fetching logic for the next page.
   */
  onLoadMore: () => Promise<void> | void
  /**
   * Whether additional pages are available.
   * Prevents extra observer callbacks when all data has been loaded.
   */
  hasMore: boolean
  /**
   * Indicates that a request is currently in flight.
   * This avoids enqueuing overlapping load-more calls.
   */
  isLoading: boolean
  /**
   * Optional scrolling container element for the IntersectionObserver root.
   * Defaults to the browser viewport when undefined.
   */
  root?: Element | null
  /**
   * Optional root margin to expand the observer area.
   * Defaults to triggering 200px before the sentinel enters the viewport.
   */
  rootMargin?: string
  /**
   * Optional IntersectionObserver threshold, defaulting to 0.
   */
  threshold?: number | number[]
}

interface UseInfiniteScrollReturn {
  /**
   * Ref callback assigned to the sentinel element at the end of a list.
   */
  sentinelRef: RefCallback<HTMLElement>
}

/**
 * Reusable IntersectionObserver-based infinite scroll hook.
 * Observes a sentinel element near the bottom of a list to trigger additional data loads.
 */
export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  isLoading,
  root,
  rootMargin = '0px 0px 200px 0px',
  threshold = 0,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const pendingRequestRef = useRef(false)

  const cleanupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      cleanupObserver()
    }
  }, [cleanupObserver])

  const sentinelRef = useCallback<RefCallback<HTMLElement>>(
    (node) => {
      cleanupObserver()

      if (!node || !hasMore) {
        return
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries

          if (
            entry?.isIntersecting &&
            hasMore &&
            !isLoading &&
            !pendingRequestRef.current
          ) {
            pendingRequestRef.current = true

            Promise.resolve(onLoadMore())
              .catch((error) => {
                console.error('🚨 useInfiniteScroll: onLoadMore failed', { error })
              })
              .finally(() => {
                pendingRequestRef.current = false
              })
          }
        },
        {
          root: root ?? null,
          rootMargin,
          threshold,
        }
      )

      observerRef.current.observe(node)
    },
    [cleanupObserver, hasMore, isLoading, onLoadMore, root, rootMargin, threshold]
  )

  return {
    sentinelRef,
  }
}

